import {
    CreateTrajectoryConfigurationInput,
    CreateTrajectoryModelMutation,
    CreateTrajectoryModelMutationVariables,
    CreateCalpuffConfigurationInput,
    CreateCalpuffConfigurationMutation,
    CreateCalpuffConfigurationMutationVariables,
    CreateCalmetWithWrfInput,
    CreateCalmetWithWrfConfigurationMutation,
    CreateCalmetWithWrfConfigurationMutationVariables,
    CreateCalmetWithProcessDataInput,
    CreateCalmetWithProcessDataConfigurationMutation,
    CreateCalmetWithProcessDataConfigurationMutationVariables,
    FetchCalmetConfigurationsQuery,
    FetchCalmetConfigurationsQueryVariables,
    FetchCalpuffConfigurationsQuery,
    FetchCalpuffConfigurationsQueryVariables,
    FetchModelConfigurationsQuery,
    FetchModelConfigurationsQueryVariables,
    FetchModellingSequencesQuery,
    FetchModellingSequencesQueryVariables,
    FetchTrajectoryModelConfigurationsQuery,
    FetchTrajectoryModelConfigurationsQueryVariables,
    FetchWrfModelConfigurationsQuery,
    FetchWrfModelConfigurationsQueryVariables,
    UpdateTrajectoryConfigurationInput,
    UpdateTrajectoryModelMutation,
    UpdateTrajectoryModelMutationVariables,
    UpdateCalpuffConfigurationPatch,
    UpdateCalpuffConfigurationMutation,
    UpdateCalpuffConfigurationMutationVariables,
    UpdateCalmetWithWrfPatch,
    UpdateCalmetWithWrfConfigurationMutation,
    UpdateCalmetWithWrfConfigurationMutationVariables,
    UpdateCalmetWithProcessDataPatch,
    UpdateCalmetWithProcessDataConfigurationMutation,
    UpdateCalmetWithProcessDataConfigurationMutationVariables,
} from 'es/app/__generated__/global';
import { ITypedNamedEntity } from 'es/app/business-logic/domain-models/Entity';
import { IModellingConfiguration, IModellingSequence } from 'es/app/business-logic/domain-models/Modelling';
import { compare } from 'es/core/helpers/string';
import ApolloProvider from 'es/core/net/ApolloProvider';
import Guid from 'es/core/types/Guid';
import { CREATE_TRAJECTORY_MODEL } from './graphql/facility.createTrajectoryModel';
import { FETCH_CALMET_CONFIGURATIONS } from './graphql/facility.fetchCalmetConfigurations';
import { FETCH_CALPUFF_CONFIGURATIONS } from './graphql/facility.fetchCalpuffConfigurations';
import { FETCH_MODEL_CONFIGURATIONS } from './graphql/facility.fetchModelConfigurations';
import { FETCH_MODELLING_SEQUENCE_QUERY } from './graphql/facility.fetchModellingSequenceQuery';
import { FETCH_TRAJECTORY_MODEL_CONFIGURATIONS } from './graphql/facility.fetchTrajectoryModelConfigurations';
import { FETCH_WRF_MODEL_CONFIGURATIONS } from './graphql/facility.fetchWrfModelConfigurations';
import { UPDATE_TRAJECTORY_MODEL } from './graphql/facility.updateTrajectoryModel';
import { CREATE_CALPUFF_CONFIGURATION } from './graphql/facility.createCalpuffConfiguration';
import { UPDATE_CALPUFF_CONFIGURATION } from './graphql/facility.updateCalpuffConfiguration';
import { CREATE_CALMET_WITH_WRF_CONFIGURATION } from './graphql/facility.createCalmetWithWrfConfiguration';
import { CREATE_CALMET_WITH_PROCESS_DATA_CONFIGURATION } from './graphql/facility.createCalmetWithProcessDataConfiguration';
import { UPDATE_CALMET_WITH_WRF_CONFIGURATION } from './graphql/facility.updateCalmetWithWrfConfiguration';
import { UPDATE_CALMET_WITH_PROCESS_DATA_CONFIGURATION } from './graphql/facility.updateCalmetWithProcessDataConfiguration';

import { FetchResult, QueryOptions } from '@apollo/client';

export interface IConfigurationEntity extends ITypedNamedEntity {
    type: ModellingConfigurationType;
}
export type FetchTrajectoryConfigurationsResult = Awaited<ReturnType<typeof fetchTrajectoryConfigurations>>;

export const MODELLING_CONFIGURATION_TYPE = {
    calpuffConfiguration: 'calpuffConfiguration',
    calmetConfiguration: 'calmetConfiguration',
} as const;

export type ModellingConfigurationType = keyof typeof MODELLING_CONFIGURATION_TYPE;

export async function fetchModellingConfigurations(): Promise<IModellingConfiguration[]> {
    const { data } = await ApolloProvider.facility().query<
        FetchModelConfigurationsQuery,
        FetchModelConfigurationsQueryVariables
    >({
        query: FETCH_MODEL_CONFIGURATIONS,
    });

    const sequences = await fetchModellingSequence();

    const { calmet, calpuff, trajectories } = data?.facility?.models?.configurations ?? {};

    const calmentConfig =
        calmet
            ?.sort((v1, v2) => compare(v1.name, v2.name))
            .sort((v1, v2) => compare(v1.__typename, v2.__typename))
            .map(config => ({
                ...config,
                type: config.__typename,
                sequences: sequences.filter(({ calmetId }) => calmetId === config.id) ?? [],
            })) ?? [];

    const calpugConfig =
        calpuff
            ?.sort((v1, v2) => compare(v1.name, v2.name))
            .map(config => ({
                ...config,
                type: config.__typename,
                sequences: sequences.filter(({ calmetId }) => calmetId === config.id) ?? [],
            })) ?? [];

    const trajectoriesConfig =
        trajectories
            ?.sort((v1, v2) => compare(v1.type, v2.type))
            .sort((v1, v2) => compare(v1.name, v2.name))
            .map(config => ({
                ...config,
                type: config.type,
                sequences: sequences.filter(({ calmetId }) => calmetId === config.id) ?? [],
            })) ?? [];

    return [...calmentConfig, ...calpugConfig, ...trajectoriesConfig];
}

async function fetchModellingSequence(): Promise<IModellingSequence[]> {
    const { data } = await ApolloProvider.facility().query<
        FetchModellingSequencesQuery,
        FetchModellingSequencesQueryVariables
    >({
        query: FETCH_MODELLING_SEQUENCE_QUERY,
    });

    return (
        data?.facility?.configurations?.sequences.flatMap(sequence => {
            if (!sequence.isEnabled) return [];
            return sequence.steps
                .filter(step => step.calmetConfiguration)
                .map(step => {
                    return {
                        id: sequence.id,
                        name: sequence.name,
                        calmetId: step.calmetConfiguration?.id,
                    };
                });
        }) ?? []
    );
}

export async function fetchWrfModelConfigurations(): Promise<string[]> {
    const { data } = await ApolloProvider.facility().query<
        FetchWrfModelConfigurationsQuery,
        FetchWrfModelConfigurationsQueryVariables
    >({
        query: FETCH_WRF_MODEL_CONFIGURATIONS,
    });
    const configs = data?.facility?.models?.wrfModelConfigurations;
    if (!configs) return [];
    return configs.flatMap(config => {
        if (!config.filename) return [];
        return config.filename;
    });
}

export async function fetchCalmetConfigurations() {
    const { data } = await ApolloProvider.facility().query<
        FetchCalmetConfigurationsQuery,
        FetchCalmetConfigurationsQueryVariables
    >({
        query: FETCH_CALMET_CONFIGURATIONS,
    });

    return (
        data?.facility?.models?.configurations?.calmet.map(config => {
            return {
                id: config.id,
                name: config.name,
                timeStep: config.timeStep,
                // todo ideally type should be Calmet with WRF or Calmet with Process data
                type: MODELLING_CONFIGURATION_TYPE.calmetConfiguration,
            };
        }) ?? []
    );
}

export async function fetchCalmetConfigurationDetails(id: Guid) {
    const queryOptions: QueryOptions<FetchCalmetConfigurationsQueryVariables> = {
        query: FETCH_CALMET_CONFIGURATIONS,
        variables: { filter: { id_in: [id] } },
    };

    const { data } = await ApolloProvider.facility().query<
        FetchCalmetConfigurationsQuery,
        FetchCalmetConfigurationsQueryVariables
    >(queryOptions);

    const [result] =
        data?.facility?.models?.configurations?.calmet.map(config => {
            const { __typename, ...base } = config;
            return {
                ...base,
                // id: config.id,
                // name: config.name,
                // timeStep: config.timeStep,
                type: config.__typename,
            };
        }) ?? [];
    if (!result) {
        throw new Error(`Calmet configuration with id ${id} not found`);
    }
    return result;
}

export async function fetchCalpuffConfigurations() {
    const queryOptions: QueryOptions<FetchCalpuffConfigurationsQueryVariables> = {
        query: FETCH_CALPUFF_CONFIGURATIONS,
    };

    const { data } = await ApolloProvider.facility().query<
        FetchCalpuffConfigurationsQuery,
        FetchCalpuffConfigurationsQueryVariables
    >(queryOptions);

    return (
        data?.facility?.models?.configurations?.calpuff.map(config => {
            return {
                id: config.id,
                name: config.name,
                description: config.description,
                settings: config.settings,
                linkedCalmetConfigurationIds: config.linkedCalmetConfigurations?.map(linked => linked.id) ?? [],
                type: MODELLING_CONFIGURATION_TYPE.calpuffConfiguration,
            };
        }) ?? []
    );
}

export async function fetchCalpuffConfigurationDetails(id: Guid) {
    const queryOptions: QueryOptions<FetchCalpuffConfigurationsQueryVariables> = {
        query: FETCH_CALPUFF_CONFIGURATIONS,
        variables: { filter: { id_in: [id] } },
    };

    const { data } = await ApolloProvider.facility().query<
        FetchCalpuffConfigurationsQuery,
        FetchCalpuffConfigurationsQueryVariables
    >(queryOptions);

    const [result] =
        data?.facility?.models?.configurations?.calpuff.map(config => {
            return {
                id: config.id,
                name: config.name,
                description: config.description,
                settings: config.settings,
                linkedCalmetConfigurationIds: config.linkedCalmetConfigurations?.map(linked => linked.id) ?? [],
                type: MODELLING_CONFIGURATION_TYPE.calpuffConfiguration,
            };
        }) ?? [];
    if (!result) {
        throw new Error(`Calpuff configuration with id ${id} not found`);
    }
    return result;
}

export async function fetchTrajectoryConfigurations(id: Guid) {
    const { data } = await ApolloProvider.facility().query<
        FetchTrajectoryModelConfigurationsQuery,
        FetchTrajectoryModelConfigurationsQueryVariables
    >({
        query: FETCH_TRAJECTORY_MODEL_CONFIGURATIONS,
        variables: {
            filter: { id_in: [id] },
        },
    });
    return data.facility?.models?.configurations?.trajectories.at(0);
}

export async function updateTrajectoryConfigurations(input: UpdateTrajectoryConfigurationInput) {
    const { data } = await ApolloProvider.facility().mutate<
        UpdateTrajectoryModelMutation,
        UpdateTrajectoryModelMutationVariables
    >({
        mutation: UPDATE_TRAJECTORY_MODEL,
        variables: { input },
    });
    return data?.facility?.modelConfigurations;
}

export async function createTrajectoryConfigurations(input: CreateTrajectoryConfigurationInput) {
    const { data } = await ApolloProvider.facility().mutate<
        CreateTrajectoryModelMutation,
        CreateTrajectoryModelMutationVariables
    >({
        mutation: CREATE_TRAJECTORY_MODEL,
        variables: { input },
    });
    return data?.facility?.modelConfigurations.trajectoryConfigurations?.createTrajectoryConfiguration;
}

export async function createCalpuffConfigurations(
    input: CreateCalpuffConfigurationInput
): Promise<FetchResult<CreateCalpuffConfigurationMutation>> {
    return await ApolloProvider.facility().mutate<
        CreateCalpuffConfigurationMutation,
        CreateCalpuffConfigurationMutationVariables
    >({
        mutation: CREATE_CALPUFF_CONFIGURATION,
        variables: { input },
    });
}

export async function updateCalpuffConfigurations({
    id,
    input,
}: {
    id: Guid;
    input: UpdateCalpuffConfigurationPatch;
}): Promise<FetchResult<UpdateCalpuffConfigurationMutation>> {
    // return the errors and other content in the response
    return await ApolloProvider.facility().mutate<
        UpdateCalpuffConfigurationMutation,
        UpdateCalpuffConfigurationMutationVariables
    >({
        mutation: UPDATE_CALPUFF_CONFIGURATION,
        variables: { id: id, input: input },
    });
}

export async function createCalmetWithWrfConfigurations(input: CreateCalmetWithWrfInput) {
    return await ApolloProvider.facility().mutate<
        CreateCalmetWithWrfConfigurationMutation,
        CreateCalmetWithWrfConfigurationMutationVariables
    >({
        mutation: CREATE_CALMET_WITH_WRF_CONFIGURATION,
        variables: { input },
    });
}

export async function createCalmetWithProcessDataConfigurations(input: CreateCalmetWithProcessDataInput) {
    return await ApolloProvider.facility().mutate<
        CreateCalmetWithProcessDataConfigurationMutation,
        CreateCalmetWithProcessDataConfigurationMutationVariables
    >({
        mutation: CREATE_CALMET_WITH_PROCESS_DATA_CONFIGURATION,
        variables: { input },
    });
}

export async function updateCalmetWithWrfConfigurations({ id, input }: { id: Guid; input: UpdateCalmetWithWrfPatch }) {
    return await ApolloProvider.facility().mutate<
        UpdateCalmetWithWrfConfigurationMutation,
        UpdateCalmetWithWrfConfigurationMutationVariables
    >({
        mutation: UPDATE_CALMET_WITH_WRF_CONFIGURATION,
        variables: { id: id, input: input },
    });
}

export async function updateCalmetWithProcessDataConfigurations({
    id,
    input,
}: {
    id: Guid;
    input: UpdateCalmetWithProcessDataPatch;
}) {
    return await ApolloProvider.facility().mutate<
        UpdateCalmetWithProcessDataConfigurationMutation,
        UpdateCalmetWithProcessDataConfigurationMutationVariables
    >({
        mutation: UPDATE_CALMET_WITH_PROCESS_DATA_CONFIGURATION,
        variables: { id: id, input: input },
    });
}
